import z from "zod";
import { autoSuggestStringRequiredSchema } from "~/services/validation/autoSuggest";
import { schemaOrEmptyString } from "~/services/validation/schemaOrEmptyString";
import { stringOptionalSchema } from "~/services/validation/stringOptional";
import { stringRequiredSchema } from "~/services/validation/stringRequired";

export type KlageParteiVerhaeltnis = "klagende" | "beklagte";
export type KlageParteiType = "natuerliche-person" | "organisation";

export const MAX_REPRESENTATIVES = 5;

export const getPluralOfKlageParteiRelationship = (
  relationship: KlageParteiVerhaeltnis,
) => (relationship === "klagende" ? "klagenden" : "beklagten");

export const adresseFields = {
  strasse: stringRequiredSchema,
  hausnummer: stringRequiredSchema,
  plz: stringRequiredSchema,
  ort: stringRequiredSchema,
  land: autoSuggestStringRequiredSchema("countries"),
};

export const personFields = {
  anrede: schemaOrEmptyString(z.enum(["herr", "frau", "none"])),
  titel: stringOptionalSchema,
  vorname: stringRequiredSchema,
  nachname: stringRequiredSchema,
};

export const commonFields = {
  id: z.uuid().optional(),
  ...adresseFields,
  parteiverhaeltnis: z.enum(["klagende", "beklagte"]),
};

export const vertretungFields = {
  ...adresseFields,
  ...personFields,
};

export const naturalPersonGesetzlicheVertretungArt = z.enum([
  "betreuer-betreuerin",
  "elternteil",
  "pfleger-pflegerin",
  "vormund-vormundin",
  "vorlaeufiger-betreuer-vorlaeufige-betreuerin",
  "sonstige-gesetzliche-vertretung",
]);

export const naturalPersonSchema = z.object({
  ...commonFields,
  ...personFields,
  type: z.literal("natuerliche-person"),
  "gesetzliche-vertretung": z
    .array(
      z.object({
        ...vertretungFields,
        art: naturalPersonGesetzlicheVertretungArt,
      }),
    )
    .max(MAX_REPRESENTATIVES)
    .optional(),
});

export const organizationGesetzlicheVertretungArt = z.enum([
  "buergermeister-buergermeisterin",
  "geschaeftsfuehrer-geschaeftsfuehrerin",
  "geschaeftsfuehrender-direktor-geschaeftsfuehrende-direktorin",
  "geschaeftsfuehrender-gesellschafter-geschaeftsfuehrende-gesellschafterin",
  "gesellschafter-gesellschafterin",
  "mitglied-einer-bgb-gesellschaft-als-gesetzliche-vertretung",
  "partner-partnerin",
  "vorstand-vorstaendin",
  "vorstandsvorsitzender-vorstandsvorsitzende",
  "sonstige-organschaftliche-vertretung-hrb",
  "sonstige-gesetzliche-vertretung-bgb",
  "sonstige-gesetzliche-vertretung",
]);

export type GesetzlicheVertretungArt =
  | z.infer<typeof naturalPersonGesetzlicheVertretungArt>
  | z.infer<typeof organizationGesetzlicheVertretungArt>;

export const organizationSchema = z.object({
  ...commonFields,
  type: z.literal("organisation"),
  orgName: stringRequiredSchema,
  orgVertretungArt: organizationGesetzlicheVertretungArt,
  orgVertretungAnrede: schemaOrEmptyString(z.enum(["herr", "frau", "none"])),
  orgVertretungTitel: stringOptionalSchema,
  orgVertretungVorname: stringRequiredSchema,
  orgVertretungNachname: stringRequiredSchema,
  "gesetzliche-vertretung": z
    .array(
      z.object({
        ...vertretungFields,
        art: organizationGesetzlicheVertretungArt,
      }),
    )
    .optional(),
});

/**
 * Represents parties to a legal case, which can be either a natural person or an organization.
 */
export const klageParteinArraySchema = z.array(
  z.union([
    naturalPersonSchema.partial({ parteiverhaeltnis: true }),
    organizationSchema.partial({ parteiverhaeltnis: true }),
  ]),
);

export type KlageParteien = z.infer<typeof klageParteinArraySchema>;
export type GesetzlicherVertreter = NonNullable<
  KlageParteien[number]["gesetzliche-vertretung"]
>[number];
